import ExpandingTextArea from "../../../../components/ExpandingTextArea"

// Lightweight controlled inputs styled with the existing input classes. Unlike the shared
// `Input` component (which owns its own state via hooks) these are fully controlled, so the
// wizard can lift all field state into the orchestrator and never lose it across steps.

export function TextField({ label, hint, value, onChange, placeholder, icon }) {
    return (
        <div className="bt-field">
            {label && <p className="bt-field-label">{hint && <span className="bt-field-hint">{hint}</span>}{label}</p>}
            <div className="input-wrapper regular-border">
                {icon && <span className="material-symbols-outlined scale-sm input-icon">{icon}</span>}
                <input value={value ?? ""} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
            </div>
        </div>
    )
}

export function AreaField({ label, hint, value, onChange, placeholder, minRows = 3, maxRows = 8 }) {
    return (
        <div className="bt-field">
            {label && <p className="bt-field-label">{hint && <span className="bt-field-hint">{hint}</span>}{label}</p>}
            <div className="input-wrapper regular-border" style={{ height: "unset", alignItems: "unset" }}>
                <ExpandingTextArea placeholder={placeholder} dynamicValue={value ?? ""} onChange={onChange} minRows={minRows} maxRows={maxRows} />
            </div>
        </div>
    )
}

// Compact +/- stepper for small bounded counts. Avoids native number-input spinners and reads as a
// first-class control: icon + label, then a pill with circular decrement/value/increment.
export function CountStepper({ icon, label, value, onChange, min = 1, max = 99, disabled }) {
    const set = (n) => onChange(Math.max(min, Math.min(max, n)))
    return (
        <div className="bt-count">
            <span className="bt-count-label">
                {icon && <span className="material-symbols-outlined scale-xs">{icon}</span>}
                {label}
            </span>
            <div className="bt-count-control">
                <button type="button" className="bt-count-btn" disabled={disabled || value <= min} onClick={() => set(value - 1)} aria-label={`Decrease ${label}`}>
                    <span className="material-symbols-outlined scale-xs">remove</span>
                </button>
                <span className="bt-count-value">{value}</span>
                <button type="button" className="bt-count-btn" disabled={disabled || value >= max} onClick={() => set(value + 1)} aria-label={`Increase ${label}`}>
                    <span className="material-symbols-outlined scale-xs">add</span>
                </button>
            </div>
        </div>
    )
}

// Segmented "Manual / AI" choice for the chapter & activity/spark composers. One sliding
// indicator sits behind the active segment (its colour mirrors the old buttons: primary-dark
// for Manual, accent for AI), so the affordance reads as a single either/or toggle.
export function ModeTabs({ mode, onChange }) {
    return (
        <div className={`mode-tabs ${mode === "ai" ? "is-ai" : ""}`} role="tablist">
            <span className="mode-tabs-indicator" aria-hidden="true" />
            <button
                type="button" role="tab" aria-selected={mode === "manual"}
                className={`mode-tab ${mode === "manual" ? "active" : ""}`}
                onClick={() => onChange("manual")}
            >
                Manual
            </button>
            <button
                type="button" role="tab" aria-selected={mode === "ai"}
                className={`mode-tab ${mode === "ai" ? "active" : ""}`}
                onClick={() => onChange("ai")}
            >
                <span className="material-symbols-outlined scale-xs">auto_awesome</span>
                <span>AI</span>
            </button>
        </div>
    )
}

export function PillGroup({ label, hint, options, value, onChange }) {
    return (
        <div className="bt-field">
            {label && <p className="bt-field-label">{hint && <span className="bt-field-hint">{hint}</span>}{label}</p>}
            <div className="flex center gap-xs wrap">
                {options.map(opt => (
                    <button
                        key={opt.value}
                        className={`bt-pill ${value === opt.value ? "bt-pill-selected" : ""}`}
                        onClick={() => onChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
